import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import { 
  Bold, Italic, Underline as UnderlineIcon, AlignLeft, 
  AlignCenter, AlignRight, Palette, Type 
} from 'lucide-react';

interface RichTextEditorProps {
  content: string;
  onChange: (content: string) => void;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({ content, onChange }) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextAlign.configure({
        types: ['paragraph'],
        alignments: ['left', 'center', 'right'],
      }),
      TextStyle,
      Color,
    ],
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  if (!editor) {
    return null;
  }

  const fontSizes = ['12px', '14px', '16px', '18px', '20px', '24px'];
  const colors = ['#000000', '#FF0000', '#00FF00', '#0000FF', '#FFA500', '#800080'];

  return (
    <div className="border rounded-lg overflow-hidden">
      <div className="bg-gray-50 p-2 border-b flex flex-wrap gap-2">
        <button
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`p-2 rounded hover:bg-gray-200 ${
            editor.isActive('bold') ? 'bg-gray-200' : ''
          }`}
          title="Gras"
        >
          <Bold className="h-4 w-4" />
        </button>
        
        <button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`p-2 rounded hover:bg-gray-200 ${
            editor.isActive('italic') ? 'bg-gray-200' : ''
          }`}
          title="Italique"
        >
          <Italic className="h-4 w-4" />
        </button>
        
        <button
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={`p-2 rounded hover:bg-gray-200 ${
            editor.isActive('underline') ? 'bg-gray-200' : ''
          }`}
          title="Souligné"
        >
          <UnderlineIcon className="h-4 w-4" />
        </button>

        <div className="h-6 w-px bg-gray-300 mx-1" />

        <button
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          className={`p-2 rounded hover:bg-gray-200 ${
            editor.isActive({ textAlign: 'left' }) ? 'bg-gray-200' : ''
          }`}
          title="Aligner à gauche"
        >
          <AlignLeft className="h-4 w-4" />
        </button>
        
        <button
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          className={`p-2 rounded hover:bg-gray-200 ${
            editor.isActive({ textAlign: 'center' }) ? 'bg-gray-200' : ''
          }`}
          title="Centrer"
        >
          <AlignCenter className="h-4 w-4" />
        </button>
        
        <button
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          className={`p-2 rounded hover:bg-gray-200 ${
            editor.isActive({ textAlign: 'right' }) ? 'bg-gray-200' : ''
          }`}
          title="Aligner à droite"
        >
          <AlignRight className="h-4 w-4" />
        </button>

        <div className="h-6 w-px bg-gray-300 mx-1" />

        <div className="relative group">
          <button
            className="p-2 rounded hover:bg-gray-200 flex items-center"
            title="Taille de police"
          >
            <Type className="h-4 w-4" />
          </button>
          <div className="absolute top-full left-0 mt-1 bg-white shadow-lg rounded-lg p-2 hidden group-hover:block z-10">
            {fontSizes.map((size) => (
              <button
                key={size}
                onClick={() => editor.chain().focus().setFontSize(size).run()}
                className="block w-full text-left px-4 py-2 hover:bg-gray-100 rounded"
                style={{ fontSize: size }}
              >
                {size}
              </button>
            ))}
          </div>
        </div>

        <div className="relative group">
          <button
            className="p-2 rounded hover:bg-gray-200 flex items-center"
            title="Couleur"
          >
            <Palette className="h-4 w-4" />
          </button>
          <div className="absolute top-full left-0 mt-1 bg-white shadow-lg rounded-lg p-2 hidden group-hover:block z-10">
            <div className="grid grid-cols-3 gap-1">
              {colors.map((color) => (
                <button
                  key={color}
                  onClick={() => editor.chain().focus().setColor(color).run()}
                  className="w-6 h-6 rounded"
                  style={{ backgroundColor: color }}
                  title={color}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      <EditorContent 
        editor={editor} 
        className="prose max-w-none p-4 min-h-[200px] focus:outline-none"
      />
    </div>
  );
};

export default RichTextEditor;